/**
 * CommandPalette — busca global com ações rápidas e navegação por teclado (TASK-22).
 *
 * Acessibilidade (RNF 9.3, WCAG 2.1 AA):
 * - role="dialog" aria-modal="true" aria-label (Req 7).
 * - Foco gerenciado: ao abrir (mount), foca o input; Esc fecha e devolve foco ao trigger.
 * - Restauração de foco no cleanup do effect (unmount) para cobrir padrão
 *   {paletteOpen && <CommandPalette />} do Shell (Finding F5).
 * - Navegação ↑↓ entre itens via input; Enter executa item ativo; Esc fecha.
 * - Modelo de foco único: aria-activedescendant no input, foco DOM fixo no input;
 *   itens NÃO recebem foco DOM (Finding F7).
 * - Focus trap: Tab/Shift+Tab mantêm foco dentro do dialog (Finding F6).
 * - Itens têm id estável para correlação com aria-activedescendant.
 * - Hover (mouseEnter) atualiza o item ativo; mouseLeave NÃO reseta o estado
 *   de teclado (Finding F9).
 * - Contorno de foco visível (CSS :focus-visible, ver módulo CSS).
 *
 * Filtro RBAC (Req 7.4):
 * - Itens de Administração/Plataforma são ocultados para usuários sem papel adequado.
 * - A filtragem é feita dentro de `useCommandPalette` via `canSeeRoute`/`canDo`.
 *
 * Ações rápidas (Req 7.2):
 * - "Nova oportunidade" e "Nova conta" requerem `create:opportunity`/`create:account`.
 * - "Localizar conta" é livre para qualquer autenticado (busca é somente leitura).
 *
 * Referências: Req 7, Req 7.1..7.4, RNF 9.3, design.md §20.15, TASK-22.
 */

import { useRef, useEffect, useCallback, type KeyboardEvent } from "react";
import { Icon } from "@azim-crm/icons";
import type { IconName } from "@azim-crm/icons";
import { useCommandPalette } from "./useCommandPalette.js";
import type { RbacContext } from "../../shared/rbac/index.js";
import styles from "./CommandPalette.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CommandPaletteProps {
  /** Se a palette está aberta. */
  open: boolean;
  /** Contexto RBAC para filtragem de itens (Req 7.4). */
  rbacCtx: RbacContext;
  /** Callback ao fechar (Esc, clique no backdrop, botão Fechar). */
  onClose(): void;
  /** Callback ao executar item: navega ou dispara ação. */
  onNavigate(path: string): void;
}

// ---------------------------------------------------------------------------
// Helper: foco dentro do dialog (Finding F6)
// ---------------------------------------------------------------------------

/**
 * Retorna todos os elementos focusáveis (tabIndex ≥ 0) dentro de um container.
 * Itens da palette têm tabIndex={-1} e não são incluídos — somente o input é retornado.
 */
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), ' +
      '[tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.tabIndex >= 0);
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Command Palette acessível com busca, filtro RBAC e ações rápidas.
 *
 * Req 7    — Command palette ⌘K com busca de telas e ações rápidas.
 * Req 7.1  — Busca de telas e contas/oportunidades por texto.
 * Req 7.2  — Ações rápidas: nova oportunidade, nova conta, localizar conta.
 * Req 7.3  — Resultados agrupados por categoria.
 * Req 7.4  — Resultados filtrados por papel (RBAC).
 * RNF 9.3  — Navegação completa por teclado: ⌘K, Esc, Enter, ↑↓.
 */
export function CommandPalette({
  open,
  rbacCtx,
  onClose,
  onNavigate,
}: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  const {
    query,
    setQuery,
    isLoading,
    groups,
    allItems,
    focusedIndex,
    setFocusedIndex,
    focusNext,
    focusPrev,
  } = useCommandPalette(rbacCtx, open);

  // ---- Gerenciamento de foco — mount/unmount (Finding F5) ------------------
  // Captura o elemento ativo ao montar e restaura no cleanup (unmount).
  // Cobre tanto o padrão {open && <CommandPalette />} do Shell (unmount real)
  // quanto o padrão de prop open=true/false mantendo o componente montado.
  useEffect(() => {
    if (!open) return;
    // Captura foco anterior ao abrir
    previousFocusRef.current = document.activeElement;
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      clearTimeout(timer);
      // Restaura foco ao trigger ao fechar (WCAG 2.4.3)
      if (
        previousFocusRef.current &&
        "focus" in previousFocusRef.current &&
        typeof (previousFocusRef.current as HTMLElement).focus === "function"
      ) {
        (previousFocusRef.current as HTMLElement).focus();
      }
    };
  }, [open]);

  // ---- Tecla Esc global ----------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // ---- Execução do item focado ---------------------------------------------
  const executeItem = useCallback(
    (index: number) => {
      const item = allItems[index];
      if (item) onNavigate(item.path);
    },
    [allItems, onNavigate],
  );

  // ---- Handler de teclado no input (RNF 9.3) --------------------------------
  // Com o modelo aria-activedescendant (Finding F7), todo o teclado passa pelo input.
  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusNext();
        break;
      case "ArrowUp":
        e.preventDefault();
        // ↑ no primeiro item volta para "sem seleção" (foco semântico no input)
        if (focusedIndex === 0) {
          setFocusedIndex(-1);
        } else {
          focusPrev();
        }
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0) {
          executeItem(focusedIndex);
        } else if (allItems.length > 0) {
          // Enter sem item selecionado executa o primeiro (comportamento padrão)
          executeItem(0);
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
    }
  };

  // ---- Focus trap: Tab/Shift+Tab (Finding F6) --------------------------------
  // Com itens tendo tabIndex={-1}, somente o input é focusável no dialog.
  // Tab e Shift+Tab são interceptados para manter o foco dentro do dialog.
  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = getFocusable(panel);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first || focusable.length === 1) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || focusable.length === 1) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [],
  );

  // ---- Scroll do item ativo para dentro da view ----------------------------
  // Finding F7: apenas scrollIntoView; sem el.focus() (foco DOM fixo no input).
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (focusedIndex < 0) return;
    const el = itemRefs.current.get(focusedIndex);
    if (el) {
      // Garante visibilidade do item ativo sem mover o foco DOM.
      el.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  if (!open) return null;

  const isEmpty =
    !isLoading &&
    allItems.length === 0;

  // ID do listbox para aria-activedescendant
  const listboxId = "az-palette-listbox";
  const activeItemId =
    focusedIndex >= 0 ? `az-palette-item-${focusedIndex}` : undefined;

  return (
    /* Overlay backdrop — clique fora fecha (Req 7, RNF 9.3) */
    <div
      className={styles.overlay}
      onMouseDown={onClose}
      aria-hidden="false"
    >
      {/* Painel principal — role="dialog" (WCAG 1.3.1, RNF 9.3) */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Busca rápida"
        className={styles.panel}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={handlePanelKeyDown}
      >
        {/* ---- Input de busca (Req 7.1) ----------------------------------- */}
        <div className={styles.inputRow}>
          <span className={styles.searchIcon} aria-hidden="true">
            <Icon name="search" size={18} />
          </span>
          <input
            ref={inputRef}
            id="az-palette-input"
            type="search"
            role="combobox"
            aria-expanded={allItems.length > 0}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeItemId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Buscar contas, ações, telas…"
            autoComplete="off"
            className={styles.input}
            aria-label="Buscar"
          />
          {isLoading && (
            <span className={styles.loadingText} aria-live="polite">
              Buscando…
            </span>
          )}
          <span className={styles.kbdHint} aria-label="Pressione Escape para fechar">
            Esc
          </span>
        </div>

        {/* ---- Resultados (Req 7.3 — agrupados) --------------------------- */}
        <div
          id={listboxId}
          role="listbox"
          aria-label="Resultados"
          className={styles.resultsList}
        >
          {isEmpty && query ? (
            <div className={styles.emptyState} role="status">
              Nenhum resultado para &ldquo;{query}&rdquo;
            </div>
          ) : isEmpty ? (
            <div className={styles.emptyState} role="status">
              Digite para buscar…
            </div>
          ) : (
            (() => {
              let flatIndex = 0;
              return groups.map((group) => (
                <div key={group.label} role="group" aria-label={group.label}>
                  {/* Cabeçalho do grupo (Req 7.3) */}
                  <div className={styles.groupLabel} aria-hidden="true">
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    const itemIndex = flatIndex++;
                    const isFocused = focusedIndex === itemIndex;
                    return (
                      <button
                        key={item.id}
                        id={`az-palette-item-${itemIndex}`}
                        ref={(el) => {
                          if (el) {
                            itemRefs.current.set(itemIndex, el);
                          } else {
                            itemRefs.current.delete(itemIndex);
                          }
                        }}
                        type="button"
                        role="option"
                        aria-selected={isFocused}
                        className={[
                          styles.item,
                          isFocused ? styles.itemFocused : "",
                        ].join(" ")}
                        onClick={() => onNavigate(item.path)}
                        onMouseEnter={() => setFocusedIndex(itemIndex)}
                        // Finding F9: onMouseLeave NÃO reseta focusedIndex para -1.
                        // Hover e navegação por teclado são desacoplados: o estado de
                        // seleção do teclado não é perturbado quando o mouse sai do item.
                        tabIndex={-1}
                      >
                        <span className={styles.itemIcon} aria-hidden="true">
                          <Icon name={item.icon as IconName} size={15} />
                        </span>
                        <span className={styles.itemLabel}>{item.label}</span>
                        <span className={styles.itemArrow} aria-hidden="true">
                          <Icon name="arrowRight" size={15} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              ));
            })()
          )}
        </div>
      </div>
    </div>
  );
}
